/**
 * SurveyManager — Super Admin "Ask Users" builder + results.
 * Create surveys (title, intro, audience, dropdown/short/long fields), activate
 * one at a time, and view responses (dropdown tallies, text answers, CSV).
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiX, FiPlus, FiTrash2, FiEdit2, FiBarChart2, FiDownload, FiArrowLeft } from 'react-icons/fi';
import { db } from '../../config/firebase';
import { collection, addDoc, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { loadSurveys, loadResponses, newFieldId, FIELD_TYPES } from '../../utils/surveyHelper';
import { DEFAULT_SIGNUP_SOURCES } from '../../config/signupSources';

const AUDIENCES = [
  { value: 'all', label: 'All companies' },
  { value: 'trial', label: 'Trial only' },
  { value: 'paid', label: 'Paid only' },
];

const emptyDraft = () => ({
  title: '', intro: '', audience: 'all', active: true, endDate: '',
  fields: [{ id: newFieldId(), label: '', type: 'dropdown', required: true, options: [], mapsTo: null }],
});

const SurveyManager = ({ open, onClose, saUser, isOwner }) => {
  const [view, setView] = useState('list'); // 'list' | 'edit' | 'results'
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resultsSurvey, setResultsSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setSurveys(await loadSurveys()); }
    catch (e) { alert('Failed to load surveys. Ensure firestore.rules allows superadmin access to surveys (re-deploy rules).'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { setView('list'); refresh(); } }, [open, refresh]);

  if (!open) return null;

  // ── Draft editing ──
  const startNew = () => { setDraft(emptyDraft()); setEditingId(null); setView('edit'); };
  const startEdit = (s) => {
    setDraft({
      title: s.title || '', intro: s.intro || '', audience: s.audience || 'all',
      active: s.active !== false, endDate: s.endDate ? s.endDate.slice(0, 10) : '',
      fields: (s.fields || []).map((f) => ({ ...f, options: f.options || [] })),
    });
    setEditingId(s.id);
    setView('edit');
  };
  const patchField = (idx, patch) => setDraft((d) => ({ ...d, fields: d.fields.map((f, i) => i === idx ? { ...f, ...patch } : f) }));
  const addField = () => setDraft((d) => ({ ...d, fields: [...d.fields, { id: newFieldId(), label: '', type: 'dropdown', required: true, options: [], mapsTo: null }] }));
  const removeField = (idx) => setDraft((d) => ({ ...d, fields: d.fields.filter((_, i) => i !== idx) }));

  const saveDraft = async () => {
    if (!isOwner) return;
    if (!draft.title.trim()) { alert('Give the survey a title.'); return; }
    const cleanFields = draft.fields
      .filter((f) => f.label.trim())
      .map((f) => ({
        id: f.id, label: f.label.trim(), type: f.type, required: !!f.required,
        options: f.type === 'dropdown' ? (f.options || []).map((o) => o.trim()).filter(Boolean) : [],
        mapsTo: f.type === 'dropdown' && f.mapsTo ? f.mapsTo : null,
      }));
    if (cleanFields.length === 0) { alert('Add at least one field with a label.'); return; }
    for (const f of cleanFields) {
      if (f.type === 'dropdown' && f.options.length === 0) { alert(`Dropdown "${f.label}" needs at least one option.`); return; }
    }

    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(), intro: draft.intro.trim(), audience: draft.audience,
        active: !!draft.active, endDate: draft.endDate ? new Date(draft.endDate).toISOString() : null,
        fields: cleanFields,
        updatedAt: new Date().toISOString(),
        updatedBy: saUser?.email || saUser?.displayName || 'superadmin',
      };
      let id = editingId;
      if (editingId) {
        await setDoc(doc(db, 'surveys', editingId), payload, { merge: true });
      } else {
        payload.createdAt = new Date().toISOString();
        payload.createdBy = payload.updatedBy;
        const ref = await addDoc(collection(db, 'surveys'), payload);
        id = ref.id;
      }
      // Only one survey active at a time — deactivate the others.
      if (payload.active) await deactivateOthers(id);
      await refresh();
      setView('list');
    } catch (e) {
      console.error('save survey failed:', e);
      alert('Failed to save. Ensure firestore.rules allows superadmin writes to surveys (re-deploy rules).');
    } finally { setSaving(false); }
  };

  const deactivateOthers = async (keepId) => {
    const all = await loadSurveys();
    await Promise.all(all.filter((s) => s.id !== keepId && s.active).map((s) => updateDoc(doc(db, 'surveys', s.id), { active: false })));
  };
  const toggleActive = async (s) => {
    try {
      await updateDoc(doc(db, 'surveys', s.id), { active: !s.active });
      if (!s.active) await deactivateOthers(s.id); // just activated → turn off others
      await refresh();
    } catch (e) { alert('Failed to update. Check rules.'); }
  };
  const removeSurvey = async (s) => {
    if (!window.confirm(`Delete survey "${s.title}"? Responses are kept in the database but it won't show or appear in the list.`)) return;
    try { await deleteDoc(doc(db, 'surveys', s.id)); await refresh(); }
    catch (e) { alert('Failed to delete. Check rules.'); }
  };

  // ── Results ──
  const openResults = async (s) => {
    setResultsSurvey(s); setResponses([]); setView('results'); setLoadingResults(true);
    try { setResponses(await loadResponses(s.id)); }
    catch (e) { alert('Failed to load responses. Check rules.'); }
    finally { setLoadingResults(false); }
  };
  const exportCsv = () => {
    const fields = resultsSurvey.fields || [];
    let csv = 'Company,' + fields.map((f) => `"${(f.label || '').replace(/"/g, '""')}"`).join(',') + ',Submitted\n';
    responses.forEach((r) => {
      const row = [`"${(r.companyName || r.companyId || '').replace(/"/g, '""')}"`];
      fields.forEach((f) => {
        const v = (r.answers && r.answers[f.id]) || '';
        row.push(`"${String(v).replace(/"/g, '""')}"`);
      });
      row.push(`"${r.submittedAt || ''}"`);
      csv += row.join(',') + '\n';
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = `Survey_${(resultsSurvey.title || 'results').replace(/[^\w\-]+/g, '_')}.csv`;
    a.click();
    window.URL.revokeObjectURL(a.href);
  };

  return (
    <Overlay onClick={() => !saving && onClose()}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Header>
          {view !== 'list' && <BackBtn onClick={() => setView('list')}><FiArrowLeft /></BackBtn>}
          <div>
            <H2>{view === 'edit' ? (editingId ? 'Edit Survey' : 'New Survey') : view === 'results' ? `Results — ${resultsSurvey?.title || ''}` : 'Ask Users'}</H2>
            <Sub>{view === 'list' ? 'Build a short survey; the company owner is asked on login until they answer.' : ''}</Sub>
          </div>
          <CloseBtn onClick={() => !saving && onClose()}><FiX /></CloseBtn>
        </Header>

        <Body>
          {view === 'list' && (
            <>
              <PrimaryBtn onClick={startNew}><FiPlus /> New Survey</PrimaryBtn>
              {loading ? <Muted>Loading…</Muted>
                : surveys.length === 0 ? <Muted>No surveys yet — create one.</Muted>
                : (
                  <List>
                    {surveys.map((s) => (
                      <Row key={s.id}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <RowTitle>{s.title}{s.active && <ActiveTag>ACTIVE</ActiveTag>}</RowTitle>
                          <RowMeta>{AUDIENCES.find((a) => a.value === s.audience)?.label || 'All'} · {(s.fields || []).length} field(s)</RowMeta>
                        </div>
                        <RowBtns>
                          <MiniBtn onClick={() => toggleActive(s)}>{s.active ? 'Deactivate' : 'Activate'}</MiniBtn>
                          <MiniBtn onClick={() => openResults(s)}><FiBarChart2 /> Results</MiniBtn>
                          <MiniBtn onClick={() => startEdit(s)}><FiEdit2 /></MiniBtn>
                          <MiniBtn $danger onClick={() => removeSurvey(s)}><FiTrash2 /></MiniBtn>
                        </RowBtns>
                      </Row>
                    ))}
                  </List>
                )}
            </>
          )}

          {view === 'edit' && (
            <>
              <FGroup><FLabel>Title *</FLabel><Input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} placeholder="e.g. Quick 30-second question" /></FGroup>
              <FGroup><FLabel>Intro (optional)</FLabel><TextArea rows={2} value={draft.intro} onChange={(e) => setDraft((d) => ({ ...d, intro: e.target.value }))} placeholder="Short line shown above the questions" /></FGroup>
              <TwoCol>
                <FGroup><FLabel>Audience</FLabel><Select value={draft.audience} onChange={(e) => setDraft((d) => ({ ...d, audience: e.target.value }))}>{AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}</Select></FGroup>
                <FGroup><FLabel>End date (optional)</FLabel><Input type="date" value={draft.endDate} onChange={(e) => setDraft((d) => ({ ...d, endDate: e.target.value }))} /></FGroup>
              </TwoCol>
              <CheckRow><input type="checkbox" checked={draft.active} onChange={(e) => setDraft((d) => ({ ...d, active: e.target.checked }))} /> Active (only one survey is active at a time — saving this active turns others off)</CheckRow>

              <Divider />
              <FLabel style={{ marginBottom: 8 }}>Questions</FLabel>
              {draft.fields.map((f, idx) => (
                <FieldBox key={f.id}>
                  <FieldTop>
                    <Input style={{ flex: 1 }} value={f.label} onChange={(e) => patchField(idx, { label: e.target.value })} placeholder="Question label (e.g. How did you hear about us?)" />
                    <Select style={{ width: 160 }} value={f.type} onChange={(e) => patchField(idx, { type: e.target.value })}>
                      {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </Select>
                    <MiniBtn $danger onClick={() => removeField(idx)}><FiTrash2 /></MiniBtn>
                  </FieldTop>
                  <CheckRow $small><input type="checkbox" checked={!!f.required} onChange={(e) => patchField(idx, { required: e.target.checked })} /> Required</CheckRow>
                  {f.type === 'dropdown' && (
                    <>
                      <FLabel $small>Options (one per line)</FLabel>
                      <TextArea rows={3} value={(f.options || []).join('\n')} onChange={(e) => patchField(idx, { options: e.target.value.split('\n') })} placeholder={'WhatsApp\nFacebook\nGoogle'} />
                      <LinkBtn onClick={() => patchField(idx, { options: [...DEFAULT_SIGNUP_SOURCES] })}>Use signup sources</LinkBtn>
                      <CheckRow $small><input type="checkbox" checked={f.mapsTo === 'heardFrom'} onChange={(e) => patchField(idx, { mapsTo: e.target.checked ? 'heardFrom' : null })} /> Use this to fill "How did you hear about us" — only asks companies whose answer is still blank</CheckRow>
                    </>
                  )}
                </FieldBox>
              ))}
              <SecondaryBtn onClick={addField}><FiPlus /> Add question</SecondaryBtn>

              <Footer>
                <MiniBtn onClick={() => setView('list')}>Cancel</MiniBtn>
                <PrimaryBtn onClick={saveDraft} disabled={saving}>{saving ? 'Saving…' : 'Save Survey'}</PrimaryBtn>
              </Footer>
            </>
          )}

          {view === 'results' && resultsSurvey && (
            <>
              <ResultsHead>
                <strong>{responses.length}</strong> response(s)
                {responses.length > 0 && <MiniBtn onClick={exportCsv}><FiDownload /> CSV</MiniBtn>}
              </ResultsHead>
              {loadingResults ? <Muted>Loading…</Muted>
                : responses.length === 0 ? <Muted>No responses yet.</Muted>
                : (resultsSurvey.fields || []).map((f) => (
                  <ResultBlock key={f.id}>
                    <ResultLabel>{f.label}</ResultLabel>
                    {f.type === 'dropdown' ? (
                      (() => {
                        const tally = {};
                        responses.forEach((r) => { const v = (r.answers && r.answers[f.id]) || '(no answer)'; tally[v] = (tally[v] || 0) + 1; });
                        return Object.entries(tally).sort((a, b) => b[1] - a[1]).map(([opt, n]) => (
                          <TallyRow key={opt}><span>{opt}</span><TallyNum>{n}</TallyNum></TallyRow>
                        ));
                      })()
                    ) : (
                      <TextAnswers>
                        {responses.filter((r) => (r.answers && r.answers[f.id])).map((r) => (
                          <TextAns key={r.id}><b>{r.companyName || r.companyId}:</b> {r.answers[f.id]}</TextAns>
                        ))}
                        {responses.filter((r) => (r.answers && r.answers[f.id])).length === 0 && <Muted>No text answers.</Muted>}
                      </TextAnswers>
                    )}
                  </ResultBlock>
                ))}
            </>
          )}
        </Body>
      </Card>
    </Overlay>
  );
};

// ── Styles ──
const Overlay = styled.div`position: fixed; inset: 0; z-index: 9000; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; padding: 16px;`;
const Card = styled.div`width: 100%; max-width: 640px; max-height: 92vh; display: flex; flex-direction: column; background: #fff; border-radius: 12px; box-shadow: 0 16px 48px rgba(0,0,0,0.3); overflow: hidden;`;
const Header = styled.div`display: flex; align-items: center; gap: 12px; padding: 18px 20px; border-bottom: 1px solid #eee;`;
const H2 = styled.h2`margin: 0; font-size: 18px; color: #1A1A1A;`;
const Sub = styled.div`font-size: 12px; color: #999; margin-top: 2px;`;
const BackBtn = styled.button`background: none; border: none; cursor: pointer; font-size: 20px; color: #666; display: flex;`;
const CloseBtn = styled.button`margin-left: auto; background: none; border: none; cursor: pointer; font-size: 20px; color: #666; display: flex;`;
const Body = styled.div`padding: 20px; overflow-y: auto;`;
const Muted = styled.div`padding: 20px; text-align: center; color: #999; font-size: 13px;`;
const List = styled.div`display: flex; flex-direction: column; gap: 8px; margin-top: 12px;`;
const Row = styled.div`display: flex; align-items: center; gap: 10px; padding: 12px; background: #f7f7f7; border: 1px solid #eee; border-radius: 8px;`;
const RowTitle = styled.div`font-size: 14px; font-weight: 700; color: #1A1A1A; display: flex; align-items: center; gap: 8px;`;
const RowMeta = styled.div`font-size: 12px; color: #777; margin-top: 2px;`;
const RowBtns = styled.div`display: flex; gap: 6px; flex-wrap: wrap;`;
const ActiveTag = styled.span`background: #E8F5E9; color: #2E7D32; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px;`;
const btn = `display: inline-flex; align-items: center; gap: 6px; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; padding: 8px 12px; border: 1px solid #d0d0d0; background: #fff; color: #333; &:hover { background: #f2f2f2; } &:disabled { opacity: 0.6; cursor: not-allowed; }`;
const MiniBtn = styled.button`${btn} padding: 6px 10px; font-size: 12px; ${(p) => p.$danger && 'color: #C62828; border-color: #f0c4c4;'}`;
const PrimaryBtn = styled.button`${btn} background: #800020; color: #fff; border-color: #800020; &:hover { opacity: 0.92; background: #800020; }`;
const SecondaryBtn = styled.button`${btn} margin-top: 8px;`;
const LinkBtn = styled.button`background: none; border: none; color: #800020; font-size: 12px; font-weight: 600; cursor: pointer; padding: 4px 0; align-self: flex-start;`;
const FGroup = styled.div`display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px;`;
const TwoCol = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 12px;`;
const FLabel = styled.label`font-size: ${(p) => p.$small ? '11px' : '13px'}; font-weight: 600; color: #444;`;
const fieldCss = `padding: 9px 12px; border: 1px solid #d5d5d5; border-radius: 8px; font-size: 13px; color: #1A1A1A; width: 100%; &:focus { outline: none; border-color: #800020; }`;
const Input = styled.input`${fieldCss}`;
const TextArea = styled.textarea`${fieldCss} resize: vertical;`;
const Select = styled.select`${fieldCss} background: #fff; cursor: pointer;`;
const CheckRow = styled.label`display: flex; align-items: center; gap: 8px; font-size: ${(p) => p.$small ? '12px' : '13px'}; color: #555; margin: 8px 0; cursor: pointer; input { cursor: pointer; }`;
const Divider = styled.div`height: 1px; background: #eee; margin: 16px 0;`;
const FieldBox = styled.div`display: flex; flex-direction: column; gap: 6px; padding: 12px; background: #fafafa; border: 1px solid #eee; border-radius: 8px; margin-bottom: 10px;`;
const FieldTop = styled.div`display: flex; gap: 8px; align-items: center;`;
const Footer = styled.div`display: flex; justify-content: flex-end; gap: 10px; margin-top: 18px; padding-top: 14px; border-top: 1px solid #eee;`;
const ResultsHead = styled.div`display: flex; align-items: center; gap: 12px; font-size: 14px; color: #333; margin-bottom: 14px;`;
const ResultBlock = styled.div`padding: 12px; background: #f7f7f7; border: 1px solid #eee; border-radius: 8px; margin-bottom: 10px;`;
const ResultLabel = styled.div`font-size: 14px; font-weight: 700; color: #1A1A1A; margin-bottom: 8px;`;
const TallyRow = styled.div`display: flex; justify-content: space-between; font-size: 13px; padding: 4px 0; border-bottom: 1px dashed #e2e2e2;`;
const TallyNum = styled.span`font-weight: 700; color: #1565C0;`;
const TextAnswers = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const TextAns = styled.div`font-size: 13px; color: #333; line-height: 1.4; b { color: #555; }`;

export default SurveyManager;
